import { useEffect, useRef, useState } from "react";
import { Layout } from "../components/Layout";
import { cachedGet, statementImageSrc } from "../lib/api";
import { Facebook, FileText } from "lucide-react";

export default function StatementsPage() {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const timeout = setTimeout(() => mounted && setLoading(false), 8000);
    cachedGet("/statements", (data) => {
      if (!mounted) return;
      setStatements(data);
      setLoading(false);
    })
      .catch(() => {})
      .finally(() => { clearTimeout(timeout); if (mounted) setLoading(false); });
    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, []);

  return (
    <Layout>
      <section
        className="relative pt-24 md:pt-32 pb-16 px-6"
        data-testid="statements-page"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 md:mb-20 fade-up">
            <div className="seal mb-6">
              <FileText size={12} /> Official Statements
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-5"
              data-testid="statements-heading"
            >
              Where I Stand
            </h1>
            <p className="text-white/60 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              Policy positions, city updates, and official statements from
              Councilmember Wood.
            </p>
          </div>

          <FacebookUpdates />

          {loading ? (
            <div className="space-y-6 mt-10" data-testid="statements-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton rounded-2xl h-56" />
              ))}
            </div>
          ) : statements.length === 0 ? null : (
            <div className="space-y-6 mt-10">
              {statements.map((s, i) => (
                <StatementCard key={s.id} statement={s} index={i} />
              ))}
            </div>
          )}


        </div>
      </section>
    </Layout>
  );
}

const StatementCard = ({ statement, index }) => {
  return (
    <article
      className="statement-card glass-strong rounded-2xl p-7 md:p-10 hover-lift fade-up"
      data-testid={`statement-${index}`}
    >
      <div className="mb-5 flex items-center gap-3 flex-wrap">
        <span className="seal" data-testid={`statement-seal-${index}`}>
          ● Official Statement
        </span>
        {statement.date_label && (
          <span className="text-white/50 text-sm" data-testid={`statement-date-${index}`}>
            {statement.date_label}
          </span>
        )}
      </div>

      <h2
        className="text-2xl md:text-3xl font-semibold text-white mb-5 leading-snug"
        data-testid={`statement-title-${index}`}
      >
        {statement.title}
      </h2>

      {statement.image_id && (
        <StatementImage statement={statement} index={index} />
      )}

      <p
        className="text-white/75 leading-relaxed whitespace-pre-line"
        data-testid={`statement-body-${index}`}
      >
        {statement.body}
      </p>

      <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
        <span className="text-white/40 text-xs tracking-wider uppercase">
          Councilmember Jacob J. Wood
        </span>
        {statement.facebook_url && (
          <a
            href={statement.facebook_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-yellow-400/80 hover:text-yellow-400 text-sm transition-colors"
            data-testid={`statement-fb-link-${index}`}
          >
            <Facebook size={14} /> Read on Facebook
          </a>
        )}
      </div>
    </article>
  );
};

const StatementImage = ({ statement, index }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      className={`mb-6 rounded-xl overflow-hidden border-[0.5px] border-white/15 ${
        loaded ? "" : "skeleton min-h-[200px]"
      }`}
    >
      <img
        src={statementImageSrc(statement)}
        alt={statement.title}
        className={`w-full h-auto object-cover max-h-[60vh] transition-opacity duration-300 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        draggable={false}
        loading={index < 2 ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        data-testid={`statement-image-${index}`}
      />
    </div>
  );
};


function FacebookUpdates() {
  const container = useRef(null);
  const [width, setWidth] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const page = "https://www.facebook.com/Councilmanwood/";
  useEffect(() => {
    const el = container.current;
    if (!el) return;
    const measure = () => setWidth(Math.min(500, Math.floor(el.getBoundingClientRect().width)));
    measure();
    if (!("ResizeObserver" in window)) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const embed = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(page)}&tabs=timeline&width=${width}&height=640&small_header=true&adapt_container_width=true&hide_cover=true&show_facepile=false`;
  return (
    <section className="glass-strong p-5 sm:p-8 mx-auto max-w-3xl" aria-labelledby="facebook-updates-title" data-testid="facebook-updates">
      <div className="flex items-center gap-3 text-yellow-400 mb-4"><Facebook size={20} /><span className="text-sm font-medium">Councilman Jacob Wood</span></div>
      <h2 id="facebook-updates-title" className="text-2xl sm:text-3xl mb-3">Updates from Facebook</h2>
      <p className="text-white/70 mb-6 leading-relaxed">Read my public posts, photos, and city updates on Facebook.</p>
      <a href={page} target="_blank" rel="noopener noreferrer" className="btn-accent inline-flex items-center justify-center gap-2 px-6 py-3" data-testid="follow-facebook">Read latest posts <span aria-hidden="true">↗</span></a>
      <div ref={container} className="mt-7 w-full max-w-[500px] mx-auto">
        {width >= 180 && <div style={{ height: expanded ? 640 : 80, overflow: "hidden", borderRadius: 12, background: "white" }}>
          <iframe src={embed} title="Councilman Wood on Facebook" width={width} height="640" style={{ border: 0, display: "block", maxWidth: "100%" }} scrolling="no" loading="lazy" allow="encrypted-media; picture-in-picture" />
        </div>}
        <button type="button" aria-expanded={expanded} onClick={() => setExpanded(v => !v)} className="mt-4 text-sm text-yellow-400 min-h-[44px]" data-testid="facebook-timeline-toggle">{expanded ? "Hide embedded timeline" : "Show embedded Facebook timeline"}</button>
        {expanded && <p className="text-white/60 text-sm mt-2">If Facebook does not display posts here, choose Read latest posts to view the full feed.</p>}
      </div>
    </section>
  );
}
